/* eslint-disable @typescript-eslint/no-empty-function */
import styled from 'styled-components'
import { DashboardCard, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'

const INBOUND_COLOR = '#38bdf8'
const OUTBOUND_COLOR = '#22c55e'

const INBOUND = { count: 78, demand: 85 }
const OUTBOUND = { count: 0, demand: 72 }

const ZONES = [
  { name: 'North Gate', count: 35 },
  { name: 'North Camp', count: 28 },
  { name: 'Processing', count: 22 },
]

const MAX_ZONE = Math.max(...ZONES.map((z) => z.count))

/* ── Styled ── */

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const NextLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.greyText};
  white-space: nowrap;
`

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
`

const DirectionCard = styled.div<{ $color: string }>`
  ${({ theme }) => theme.container};
  border-radius: 10px;
  padding: 14px 12px 12px;
  border-left: 3px solid ${(p) => p.$color};
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DirLabel = styled.span<{ $color: string }>`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(p) => p.$color};
`

const DirCount = styled.span`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${({ theme }) => theme.textColor};
  line-height: 1.1;
`

const DirDemand = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.greyText};
  margin-bottom: 6px;
`

const TrackWrap = styled.div`
  position: relative;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  overflow: hidden;
`

const Track = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.textColor};
  opacity: 0.1;
`

const Fill = styled.div<{ $color: string; $pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => p.$pct}%;
  max-width: 100%;
  background: ${(p) => p.$color};
  border-radius: 2px;
`

const ZoneSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ZoneLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.greyText};
  margin-bottom: 2px;
`

const ZoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const ZoneName = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.greyText};
  min-width: 80px;
`

const ZoneBarWrap = styled.div`
  flex: 1;
  position: relative;
  height: 3px;
  border-radius: 2px;
  overflow: hidden;
`

const ZoneTrack = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.textColor};
  opacity: 0.1;
`

const ZoneFill = styled.div<{ $pct: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => p.$pct}%;
  background: #ff9900;
  border-radius: 2px;
`

const ZoneCount = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textColor};
  min-width: 28px;
  text-align: right;
`

/* ── Component ── */

export const HrTransportationWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const inboundPct = INBOUND.demand > 0 ? Math.round((INBOUND.count / INBOUND.demand) * 100) : 0
  const outboundPct = OUTBOUND.demand > 0 ? Math.round((OUTBOUND.count / OUTBOUND.demand) * 100) : 0

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => onDragStart('transportation', 1)}
      onDrop={onDragDrop}
      onDragEnd={(e) => e.preventDefault()}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('transportation')
      }}
      width='1fr'
      style={{ gridArea: 'transportation', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('transportation')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}

      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0 }}>
          TRANSPORTATION
        </TitleText>
        <NextLabel>Next ep. 14:30</NextLabel>
      </CardHeader>

      <CardsRow>
        <DirectionCard $color={INBOUND_COLOR}>
          <DirLabel $color={INBOUND_COLOR}>Inbound</DirLabel>
          <DirCount>{INBOUND.count}</DirCount>
          <DirDemand>of {INBOUND.demand} demand</DirDemand>
          <TrackWrap>
            <Track />
            <Fill $color={INBOUND_COLOR} $pct={inboundPct} />
          </TrackWrap>
        </DirectionCard>

        <DirectionCard $color={OUTBOUND_COLOR}>
          <DirLabel $color={OUTBOUND_COLOR}>Outbound</DirLabel>
          <DirCount>{OUTBOUND.count}</DirCount>
          <DirDemand>of {OUTBOUND.demand} demand</DirDemand>
          <TrackWrap>
            <Track />
            <Fill $color={OUTBOUND_COLOR} $pct={outboundPct} />
          </TrackWrap>
        </DirectionCard>
      </CardsRow>

      <ZoneSection>
        <ZoneLabel>Zone demand</ZoneLabel>
        {ZONES.map((z) => (
          <ZoneRow key={z.name}>
            <ZoneName>{z.name}</ZoneName>
            <ZoneBarWrap>
              <ZoneTrack />
              <ZoneFill $pct={MAX_ZONE > 0 ? Math.round((z.count / MAX_ZONE) * 100) : 0} />
            </ZoneBarWrap>
            <ZoneCount>{z.count}</ZoneCount>
          </ZoneRow>
        ))}
      </ZoneSection>
    </DashboardCard>
  )
}
